import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import { useForm } from 'react-hook-form';
import { switchProp } from 'styled-tools';
import { useStoreStock, useTranslation } from '$hooks';
import { getStores, getStoresWithZipOrString } from '$services/store.service';
import { IStoreWithStock } from '$models/product-types';
import { Flex } from '$components/layouts';
import { getGeoLocation } from '$services/geolocator.service';
import { sortStoresWithZip } from '$lib/helpers';
import { theme } from '$theme';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { Checkbox } from '$components/elements/checkbox';
import { InputField } from '$components/elements/input-field';
import { SidePanel } from '$components/elements/side-panel';
import { SvgIcon } from '$components/elements/svg-icon';
import { PickupStoreItem } from './pickup-store-item';
import { useTempBasket } from '~/store/temp-basket';
import { checkoutPageUrl } from '$constants';
import { IStoreInformation } from '$models';

const defaultShownStores = 4;

type FormData = {
    search: string;
};

type Props = {
    productStock: Record<string, number> | undefined;
    selectedStore?: IStoreInformation;
    itemNumber?: string;
    isOpen: boolean;
    stockCount?: number;
    selectedZipCode?: string;
    setIsOpen: (isOpen: boolean) => void;
    onStoreSelection?: (id?: string) => void;
    canPickup: boolean;
};

export const PickupPanel: FC<Props> = ({
    productStock,
    selectedStore,
    isOpen,
    itemNumber,
    stockCount,
    selectedZipCode,
    setIsOpen,
    onStoreSelection,
    canPickup,
}): JSX.Element => {
    const { translate } = useTranslation();
    const { createPickupBasket } = useTempBasket();
    const { push } = useRouter();
    const [storesWithStock, setStoresWithStock] = useState<IStoreWithStock[]>();
    const [shownStores, setShownStores] = useState(defaultShownStores);
    const [displayShowMore, setDisplayShowMore] = useState(false);
    const [isGeoSearch, setIsGeoSearch] = useState(false);
    const [savedCoords, setSavedCoords] = useState<GeolocationCoordinates>();
    const [showWithStock, setShowWithStock] = useState(false);
    const [initial, setInitial] = useState(true);
    const [isCreatingTempBasket, setIsCreatingTempBasket] = useState(false);
    const {
        displayShowMore: filterDisplayShowMore,
        filteredDisplayStores,
        onGeoSearchFilteredStores,
        onSearchFilteredStores,
        onShowMore: filteredOnShowMore,
        isLoading: isLoadingFilter,
        setIsLoading: setIsLoadingFilter,
    } = useStoreStock(productStock, shownStores);

    useEffect(() => {
        if (selectedZipCode && !selectedStore && isOpen) {
            setValue('search', selectedZipCode, { shouldValidate: true, shouldDirty: true });
            onSearchStores(selectedZipCode);
        }
    }, [selectedZipCode, selectedStore, isOpen, productStock]);

    useEffect(() => {
        if (showWithStock) {
            if (isGeoSearch) {
                onGeoSearchFilteredStores(savedCoords?.latitude, savedCoords?.longitude);
            } else {
                onSearchFilteredStores(getValues('search'));
            }
        } else {
            setIsLoadingFilter(false);
        }
    }, [showWithStock]);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm<FormData>();

    const onSubmit = handleSubmit((data) => {
        setIsGeoSearch(false);
        onSearchStores(data.search);
        if (showWithStock) {
            onSearchFilteredStores(data.search);
        }
    });

    const onSearchStores = async (search: string, newLimit?: number) => {
        setInitial(false);
        const limit = newLimit || defaultShownStores;
        setShownStores(limit);
        let stores = await getStoresWithZipOrString(search, limit);
        let formattedStores: IStoreWithStock[] = [];
        if (stores) {
            stores = sortStoresWithZip(stores, search);
            setDisplayShowMore(stores.length >= limit);
            formattedStores = stores.map((store) =>
                store.customerId
                    ? {
                          ...store,
                          stock: productStock?.[store.customerId] || -1,
                      }
                    : { ...store, stock: -1 }
            );
        }
        setStoresWithStock(formattedStores);
    };

    const onGeoSearchStores = async (lat?: number, lng?: number, newLimit?: number) => {
        setInitial(false);
        if (!lat || !lng) {
            return;
        }
        const limit = newLimit || defaultShownStores;
        setShownStores(limit);
        const stores = await getStores(lat, lng, limit);
        let formattedStores: IStoreWithStock[] = [];
        if (stores) {
            setDisplayShowMore(stores.length >= limit);
            formattedStores = stores.map((store) =>
                store.customerId
                    ? {
                          ...store,
                          stock: productStock?.[store.customerId] || -1,
                      }
                    : { ...store, stock: -1 }
            );
        }
        setStoresWithStock(formattedStores);
    };

    const onShowMore = () => {
        const newLimit = shownStores + 3;
        isGeoSearch
            ? onGeoSearchStores(savedCoords?.latitude, savedCoords?.longitude, newLimit)
            : onSearchStores(getValues('search'), newLimit);
    };

    const getGeoCoords = () => {
        getGeoLocation(
            ({ coords }) => {
                setIsGeoSearch(true);
                setSavedCoords(coords);
                onGeoSearchStores(coords.latitude, coords.longitude, defaultShownStores);
            },
            () => setIsGeoSearch(false)
        );
    };

    const onPickup = async (store?: IStoreInformation) => {
        if (!itemNumber && !store) {
            return;
        }
        setIsCreatingTempBasket(true);
        const success = await createPickupBasket(itemNumber, store, selectedZipCode);
        setIsCreatingTempBasket(false);

        if (success) {
            push(checkoutPageUrl);
        }
    };

    return (
        <SidePanel
            onChange={setIsOpen}
            show={isOpen}
            title={translate('productPage.clickCollect.panelHeader')}
            mobileStyle
        >
            <StyledForm onSubmit={onSubmit}>
                <InputContainer gap="medium">
                    <SearchInputField
                        label={translate('findStore.zipOrCity')}
                        error={errors.search}
                        defaultValue={selectedZipCode}
                        {...register('search', {
                            required: translate('validation.error.fieldRequired'),
                        })}
                    />
                    <SearchIconButton type="submit">
                        <AccessibleIcon.Root label={translate('general.search')}>
                            <SvgIcon svg="loupe" />
                        </AccessibleIcon.Root>
                    </SearchIconButton>
                </InputContainer>
                <HelpText>{translate('productPage.productStock.modalInputHelpText')}</HelpText>
            </StyledForm>
            <MainContent>
                {initial && selectedStore?.customerId && (
                    <ItemContainer column>
                        <PickupStoreItem
                            key={selectedStore.customerId}
                            store={{
                                ...selectedStore,
                                stock: productStock?.[selectedStore?.customerId] || -1,
                            }}
                            isSelected={selectedStore.customerId === selectedStore?.customerId}
                            canPickup={canPickup}
                            onStoreSelection={onStoreSelection}
                            onPickup={onPickup}
                            isLoading={isCreatingTempBasket}
                        />
                    </ItemContainer>
                )}
                {initial && (
                    <NoSelectionContainer column alignItems="center" gap="medium">
                        <NoSelectionText center color={theme.colors.textSubtle}>
                            {translate('productPage.clickCollect.noSelectionText')}
                        </NoSelectionText>
                        <Button onClick={getGeoCoords}>
                            <ButtonIcon color={theme.colors.white} size={24} svg="pin2" />
                            {translate('productPage.clickCollect.useLocationButtonText')}
                        </Button>
                    </NoSelectionContainer>
                )}
                {!initial && (
                    <>
                        {!isLoadingFilter && showWithStock && !filteredDisplayStores.length && (
                            <NoStoresText>
                                {translate('productPage.productStock.filterNoStoresWithStockFound')}
                            </NoStoresText>
                        )}
                        {!!(showWithStock ? filteredDisplayStores : storesWithStock)?.length && (
                            <ItemContainer column gap="small">
                                <Text variant="smallHeader" fontWeight="Bold">
                                    {translate('productPage.clickCollect.storeSectionHeader')}
                                </Text>
                                {stockCount ? (
                                    <Flex spacingBottom="small" alignItems="center">
                                        <Checkbox
                                            checked={showWithStock}
                                            onChange={(e) => setShowWithStock(e.target.checked)}
                                            disabled={!isGeoSearch && initial}
                                        />

                                        <Text
                                            color={
                                                !isGeoSearch && initial ? theme.colors.grey30 : theme.colors.primaryDark
                                            }
                                        >
                                            {translate('productPage.productStock.filterStoresWithStock')}
                                        </Text>
                                    </Flex>
                                ) : (
                                    <Flex spacingBottom="small" alignItems="center">
                                        <SvgIcon svg="info" color={theme.colors.grey30} />

                                        <Text
                                            variant="smallHeader"
                                            size="12px"
                                            color={theme.colors.grey30}
                                            style={{ marginLeft: theme.space[2] }}
                                        >
                                            {translate('productPage.productStock.filterNoStoresWithStock')}
                                        </Text>
                                    </Flex>
                                )}
                                <Flex column gap="extrasmall">
                                    {(showWithStock ? filteredDisplayStores : storesWithStock)?.map((store) => (
                                        <PickupStoreItem
                                            key={store.customerId}
                                            store={store}
                                            isSelected={store.customerId === selectedStore?.customerId}
                                            canPickup={canPickup}
                                            onStoreSelection={onStoreSelection}
                                            onPickup={onPickup}
                                            isLoading={isCreatingTempBasket}
                                        />
                                    ))}
                                </Flex>
                            </ItemContainer>
                        )}
                        {showWithStock ? (
                            <>
                                {filteredDisplayStores && filterDisplayShowMore && (
                                    <Flex justifyContent="center">
                                        <ShowMore variant="link" onClick={filteredOnShowMore}>
                                            {translate('productPage.productStock.modalShowMoreButton')}
                                        </ShowMore>
                                    </Flex>
                                )}
                            </>
                        ) : (
                            <>
                                {storesWithStock && displayShowMore && (
                                    <Flex justifyContent="center">
                                        <ShowMore variant="link" onClick={onShowMore}>
                                            {translate('productPage.productStock.modalShowMoreButton')}
                                        </ShowMore>
                                    </Flex>
                                )}
                            </>
                        )}
                    </>
                )}
            </MainContent>
        </SidePanel>
    );
};

const SearchInputField = styled(InputField)({
    flex: 1,
    overflow: 'hidden',
});

const NoStoresText = styled(Text)(({ theme }) => ({
    marginTop: theme.space[2],
}));

const InputContainer = styled(Flex)(({ theme }) => ({
    marginTop: theme.space[2],
    position: 'relative',
}));

const StyledForm = styled.form(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: `0 ${theme.space[3]}`,
    marginBottom: theme.space[4],
}));

const MainContent = styled(Flex)({
    flexDirection: 'column',
    height: '100%',
});

const NoSelectionContainer = styled(Flex)({
    marginTop: '30%',
});

const ButtonIcon = styled(SvgIcon)(({ theme }) => ({
    marginRight: theme.space[3],
}));

const NoSelectionText = styled(Text)({
    maxWidth: '270px',
});

const ShowMore = styled(Button)(({ theme }) => ({
    color: theme.colors.grey30,
    marginTop: theme.space[5],
    marginBottom: theme.space[7],
}));

const ItemContainer = styled(Flex)(({ theme }) => ({
    padding: `0 ${theme.space[3]}`,
}));

const SearchIconButton = styled.button<{ disabled?: boolean }>(
    ({ theme: { space, fontSizes } }) => ({
        position: 'absolute',
        top: '50%',
        right: space[4],
        transform: 'translateY(-50%)',

        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        fontSize: fontSizes.lg,
        border: 'none',
        background: 'none',
        cursor: 'pointer',

        transition: 'opacity 200ms ease',
    }),
    switchProp('disabled', {
        true: {
            opacity: 0,
            pointerEvents: 'none',
        },
        false: {
            opacity: 1,
            pointerEvents: 'all',
        },
    })
);

const HelpText = styled(Text)(({ theme }) => ({
    color: theme.colors.grey40,
    fontSize: theme.fontSizes.xxs,
    marginTop: theme.space[2],
    marginBottom: theme.space[4],
}));
